/**
 * ARAM Platform - Notifications Store
 * Core Desarrollos S.A.S - Copyright 2024-2025
 */

import { create } from 'zustand'
import { apiClient } from '@/lib/api'

interface Notification {
  id: number
  title: string
  body: string
  type: string
  data?: Record<string, unknown>
  read_at: string | null
  created_at: string
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  
  // Actions
  fetchNotifications: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markAsRead: (id: number) => Promise<void>
  markAllAsRead: () => Promise<void>
}

export const useNotificationStore = create<NotificationState>((set, _get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  
  fetchNotifications: async () => {
    set({ isLoading: true })
    try {
      const response = await apiClient.get('/notifications')
      const rows = response.data.notifications ?? response.data.items ?? []
      set({
        notifications: rows.map((row: {
          id: number
          subject: string
          body: string
          channel: string
          read_at: string | null
          created_at: string
        }) => ({
          id: row.id,
          title: row.subject,
          body: row.body,
          type: row.channel,
          read_at: row.read_at,
          created_at: row.created_at,
        })),
        isLoading: false,
      })
    } catch {
      set({ isLoading: false })
    }
  },
  
  fetchUnreadCount: async () => {
    try {
      const response = await apiClient.get('/notifications/stats')
      set({ unreadCount: response.data.unread ?? 0 })
    } catch {
      // Ignore errors
    }
  },
  
  markAsRead: async (id: number) => {
    try {
      await apiClient.post(`/notifications/${id}/mark-read`)
      
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch {
      // Ignore errors
    }
  },
  
  markAllAsRead: async () => {
    try {
      await apiClient.post('/notifications/mark-all-read')
      
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          read_at: n.read_at || new Date().toISOString(),
        })),
        unreadCount: 0,
      }))
    } catch {
      // Ignore errors
    }
  },
}))
